import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

import type { User } from "@/features/users/schemas/user";
import { getCurrentUserFromFirestore } from "@/features/users/services/currentUserService";

/**
 * Current user context type definition
 * This context handles only the Firestore user data, not authentication
 */
interface CurrentUserContextType {
  /** Current user data from Firestore or null if not found/loading */
  currentUserData: User | null;
  /** Whether the Firestore user data is being loaded */
  loading: boolean;
  /** Error message if loading user data failed */
  error: string | null;
  /** Function to manually refresh user data */
  refreshUserData: () => Promise<void>;
  /** Function to clear user data (useful for logout) */
  clearUserData: () => void;
}

/**
 * Props for the CurrentUserProvider component
 */
interface CurrentUserProviderProps {
  children: ReactNode;
  /** Firebase Auth UID to load user data for */
  authUid: string | null;
}

/**
 * Context for current user data from Firestore
 * @constant {Context<CurrentUserContextType | undefined>} CurrentUserContext - Context for current user data
 */
const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

/**
 * Component that provides current user data from Firestore
 * This provider is separate from authentication and only handles user data
 *
 * @param {CurrentUserProviderProps} props - Component props
 * @returns {React.JSX.Element} CurrentUserProvider component
 *
 * @example
 * ```typescript
 * <CurrentUserProvider authUid={authUser?.uid}>
 *   <App />
 * </CurrentUserProvider>
 * ```
 */
export function CurrentUserProvider({
  children,
  authUid,
}: CurrentUserProviderProps): React.JSX.Element {
  const [currentUserData, setCurrentUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load user data from Firestore
   */
  const loadUserData = async (uid: string | null) => {
    if (!uid) {
      setCurrentUserData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getCurrentUserFromFirestore(uid);

      if (result.success && result.data) {
        setCurrentUserData(result.data);
      } else {
        setCurrentUserData(null);
        setError(result.error || "Failed to load user data");
      }
    } catch (error) {
      setCurrentUserData(null);
      setError(error instanceof Error ? error.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Manually refresh user data
   */
  const refreshUserData = async () => {
    if (authUid) {
      await loadUserData(authUid);
    }
  };

  /**
   * Clear user data (useful for logout)
   */
  const clearUserData = () => {
    setCurrentUserData(null);
    setError(null);
    setLoading(false);
  };

  /**
   * Effect to load user data when authUid changes
   */
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadUserData(authUid);
  }, [authUid]);

  /**
   * Valor del contexto de usuario actual
   * @constant {CurrentUserContextType} value - Valor del contexto de usuario actual
   */
  const value: CurrentUserContextType = {
    currentUserData,
    loading,
    error,
    refreshUserData,
    clearUserData,
  };

  return (
    <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>
  );
}

export { CurrentUserContext };
